import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  CircularProgress,
  Alert,
  LinearProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  Security as SecurityIcon,
  Gavel as LicenseIcon,
  Update as UpdateIcon,
  BugReport as BugIcon,
  DeleteSweep as ClearIcon,
} from '@mui/icons-material';
import { vendorAPI, VendorDependency, DependencyStats } from '../../services/vendorAPI';

interface RiskFactor {
  type: string;
  severity: string;
  message: string;
}

const DependencyScanner: React.FC = () => {
  const [dependencies, setDependencies] = useState<VendorDependency[]>([]);
  const [stats, setStats] = useState<DependencyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('risk');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadDependencies();
    loadStats();
  }, [filterType, sortBy]);

  const loadDependencies = async () => {
    try {
      setLoading(true);
      const filters: Record<string, unknown> = { sort_by: sortBy };
      if (filterType === 'outdated') filters.is_outdated = true;
      if (filterType === 'deprecated') filters.is_deprecated = true;
      if (filterType === 'copyleft') filters.is_copyleft = true;
      if (filterType === 'vulnerable') filters.has_vulnerabilities = true;
      if (searchTerm) filters.search = searchTerm;

      const data = await vendorAPI.getDependencies(filters as Parameters<typeof vendorAPI.getDependencies>[0]);
      setDependencies(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load dependencies');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await vendorAPI.getDependencyStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load dependency stats');
    }
  };

  const handleScan = async () => {
    try {
      setScanning(true);
      setError(null);
      await vendorAPI.scanDependencies();
      await loadDependencies();
      await loadStats();
    } catch (err: any) {
      setError(err.message || 'Failed to scan dependencies');
    } finally {
      setScanning(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Are you sure you want to clear all dependency data? This action cannot be undone.')) {
      return;
    }
    try {
      await vendorAPI.clearDependencies();
      setDependencies([]);
      setStats(null);
    } catch (err: any) {
      setError(err.message || 'Failed to clear dependencies');
    }
  };

  const handleSearch = () => {
    loadDependencies();
  };

  const toggleRowExpand = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getRiskColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 50) return 'error';
    if (score >= 25) return 'warning';
    return 'success';
  };

  const getRiskLabel = (score: number): string => {
    if (score >= 50) return 'High';
    if (score >= 25) return 'Medium';
    return 'Low';
  };

  const filteredDependencies = dependencies.filter(dep =>
    !searchTerm || dep.package_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total Packages
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.directDependencies} direct dependencies
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ borderColor: stats.withVulnerabilities > 0 ? 'error.main' : 'divider' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <BugIcon color={stats.withVulnerabilities > 0 ? 'error' : 'disabled'} />
                <Typography variant="overline" color="text.secondary">
                  Vulnerabilities
                </Typography>
              </Box>
              <Typography variant="h4" color={stats.withVulnerabilities > 0 ? 'error.main' : 'inherit'}>
                {stats.withVulnerabilities}
              </Typography>
              <Box display="flex" gap={1} mt={0.5}>
                {stats.criticalVulnerabilities > 0 && (
                  <Chip size="small" label={`${stats.criticalVulnerabilities} Critical`} color="error" />
                )}
                {stats.highVulnerabilities > 0 && (
                  <Chip size="small" label={`${stats.highVulnerabilities} High`} color="warning" />
                )}
              </Box>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ borderColor: stats.outdated > 0 ? 'warning.main' : 'divider' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <UpdateIcon color={stats.outdated > 0 ? 'warning' : 'disabled'} />
                <Typography variant="overline" color="text.secondary">
                  Outdated
                </Typography>
              </Box>
              <Typography variant="h4" color={stats.outdated > 0 ? 'warning.main' : 'inherit'}>
                {stats.outdated}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.deprecated} deprecated
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ borderColor: stats.copyleft > 0 ? 'info.main' : 'divider' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <LicenseIcon color={stats.copyleft > 0 ? 'info' : 'disabled'} />
                <Typography variant="overline" color="text.secondary">
                  License Issues
                </Typography>
              </Box>
              <Typography variant="h4" color={stats.copyleft > 0 ? 'info.main' : 'inherit'}>
                {stats.copyleft}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                copyleft licenses
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search packages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterType}
              label="Filter"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">All Packages</MenuItem>
              <MenuItem value="vulnerable">With Vulnerabilities</MenuItem>
              <MenuItem value="outdated">Outdated</MenuItem>
              <MenuItem value="deprecated">Deprecated</MenuItem>
              <MenuItem value="copyleft">Copyleft License</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="risk">Risk Score</MenuItem>
              <MenuItem value="vulnerabilities">Vulnerabilities</MenuItem>
              <MenuItem value="name">Package Name</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={dependencies.length === 0}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={scanning ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={handleScan}
            disabled={scanning}
          >
            {scanning ? 'Scanning...' : 'Scan package.json'}
          </Button>
        </Box>
      </Box>

      {scanning && (
        <Box mb={2}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Scanning dependencies and fetching npm registry data...
          </Typography>
        </Box>
      )}

      {loading && !scanning ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredDependencies.length === 0 ? (
        <Box textAlign="center" py={6}>
          <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Dependencies Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Click "Scan package.json" to analyze your project dependencies
          </Typography>
          <Button variant="contained" onClick={handleScan} disabled={scanning}>
            Start Scan
          </Button>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50} sx={{ bgcolor: 'background.paper' }}>S.N.</TableCell>
                <TableCell width={40} sx={{ bgcolor: 'background.paper' }}></TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Package</TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Version</TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Latest</TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>License</TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Risk</TableCell>
                <TableCell sx={{ bgcolor: 'background.paper' }}>Issues</TableCell>
                <TableCell align="right" sx={{ bgcolor: 'background.paper' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDependencies.map((dep, index) => (
                <React.Fragment key={dep.id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpand(dep.id)}
                      >
                        {expandedRows.has(dep.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {dep.package_name}
                      </Typography>
                      {dep.is_direct_dependency && (
                        <Chip size="small" label="Direct" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {dep.package_version}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontFamily="monospace">
                          {dep.latest_version || '-'}
                        </Typography>
                        {dep.is_outdated && (
                          <Tooltip title={`${dep.versions_behind || 'Multiple'} versions behind`}>
                            <WarningIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          {dep.license_type || 'Unknown'}
                        </Typography>
                        {dep.is_copyleft && (
                          <Tooltip title="Copyleft license - may require code disclosure">
                            <WarningIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${dep.risk_score} - ${getRiskLabel(dep.risk_score)}`}
                        color={getRiskColor(dep.risk_score)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {dep.vulnerability_count > 0 && (
                          <Tooltip title={`${dep.vulnerability_count} vulnerabilities`}>
                            <Chip
                              size="small"
                              icon={<BugIcon />}
                              label={dep.vulnerability_count}
                              color="error"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {dep.is_deprecated && (
                          <Tooltip title={dep.deprecation_message || 'Deprecated'}>
                            <Chip size="small" label="Deprecated" color="error" variant="outlined" />
                          </Tooltip>
                        )}
                        {dep.is_eol && (
                          <Chip size="small" label="EOL" color="error" variant="outlined" />
                        )}
                        {!dep.vulnerability_count && !dep.is_deprecated && !dep.is_eol && !dep.is_outdated && !dep.is_copyleft && (
                          <CheckCircleIcon fontSize="small" color="success" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {dep.registry_url && (
                        <Tooltip title="View on npm">
                          <IconButton
                            size="small"
                            component="a"
                            href={dep.registry_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, borderBottom: expandedRows.has(dep.id) ? 1 : 0 }}>
                      <Collapse in={expandedRows.has(dep.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>
                                Package Details
                              </Typography>
                              <Box display="flex" flexDirection="column" gap={1}>
                                {dep.homepage_url && (
                                  <Link href={dep.homepage_url} target="_blank" rel="noopener">
                                    Homepage
                                  </Link>
                                )}
                                {dep.repository_url && (
                                  <Link href={dep.repository_url.replace('git+', '').replace('.git', '')} target="_blank" rel="noopener">
                                    Repository
                                  </Link>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                  Last scanned: {dep.last_scan_date ? new Date(dep.last_scan_date).toLocaleString() : 'Never'}
                                </Typography>
                              </Box>
                            </Box>
                            <Box>
                              {dep.risk_factors && (dep.risk_factors as RiskFactor[]).length > 0 && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Risk Factors
                                  </Typography>
                                  <Box display="flex" flexDirection="column" gap={1}>
                                    {(dep.risk_factors as RiskFactor[]).map((factor, idx) => (
                                      <Box key={idx} display="flex" alignItems="center" gap={1}>
                                        {factor.severity === 'critical' && <ErrorIcon fontSize="small" color="error" />}
                                        {factor.severity === 'high' && <WarningIcon fontSize="small" color="error" />}
                                        {factor.severity === 'medium' && <WarningIcon fontSize="small" color="warning" />}
                                        {factor.severity === 'low' && <InfoIcon fontSize="small" color="info" />}
                                        <Typography variant="body2">
                                          {factor.message}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {stats?.lastScanDate && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Last scan: {new Date(stats.lastScanDate).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
};

export default DependencyScanner;
